import { supabase } from './supabase'

/**
 * Tournament invites — organizers invite existing users to their event.
 *
 * All access goes through SECURITY DEFINER RPCs (see migration
 * 20260803010000_tournament_invites.sql): RLS hides other users' invites and
 * registrations, so authorization lives inside the functions. Design doc:
 * docs/tournament-invites-plan.md.
 */

export type InviteStatus = 'pending' | 'accepted' | 'declined' | 'cancelled'

/** An invite as seen by the invitee (Invites tab, event-page banner). */
export interface MyInvite {
  inviteId: string
  message: string | null
  invitedAt: string
  inviter: { gamertag: string; avatar: string | null }
  tournamentId: string
  slug: string
  title: string
  startsAt: string
  timezone: string
  coverImage: string | null
  gameName: string
  gameSlug: string
  entryFeeCents: number
  prizePoolCents: number
  accent: { from: string; to: string }
}

/** An invite as seen by an organizer on the manage page. */
export interface SentInvite {
  inviteId: string
  inviteeId: string
  gamertag: string
  avatar: string | null
  status: InviteStatus
  message: string | null
  invitedAt: string
  respondedAt: string | null
}

/** Sidebar data for the Invite Guests modal. */
export interface InviteSources {
  matches: number
  pastGuests: number
  events: { id: string; title: string; startsAt: string; guests: number }[]
}

export type InviteSourceKey = 'matches' | 'past_guests' | 'event'

/** A person the organizer can invite. */
export interface InviteCandidate {
  userId: string
  gamertag: string
  avatar: string | null
}

/** Send invites to a batch of users. Returns how many were sent vs skipped. */
export async function sendInvites(
  tournamentId: string,
  inviteeIds: string[],
  message?: string
): Promise<{ sent: number; skipped: number } | { error: string }> {
  const { data, error } = await supabase.rpc('send_tournament_invites', {
    p_tournament_id: tournamentId,
    p_invitee_ids: inviteeIds,
    p_message: message?.trim() || null,
  })
  if (error) {
    console.error('sendInvites error:', error.message)
    return { error: error.message }
  }
  const r = data as { sent: number; skipped: number }
  return { sent: r?.sent ?? 0, skipped: r?.skipped ?? 0 }
}

/** Accept or decline an invite (invitee only). */
export async function respondToInvite(
  inviteId: string,
  accept: boolean
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.rpc('respond_to_tournament_invite', {
    p_invite_id: inviteId,
    p_accept: accept,
  })
  if (error) {
    console.error('respondToInvite error:', error.message)
    return { ok: false, error: error.message }
  }
  return { ok: true }
}

/** Withdraw a pending invite (organizer only). */
export async function cancelInvite(inviteId: string): Promise<boolean> {
  const { error } = await supabase.rpc('cancel_tournament_invite', {
    p_invite_id: inviteId,
  })
  if (error) console.error('cancelInvite error:', error.message)
  return !error
}

/** The current user's pending invites, newest first. */
export async function getMyInvites(): Promise<MyInvite[]> {
  const { data, error } = await supabase.rpc('my_tournament_invites')
  if (error || !data) {
    if (error) console.error('getMyInvites error:', error.message)
    return []
  }
  return (data as Array<{
    invite_id: string
    message: string | null
    invited_at: string
    inviter_gamertag: string
    inviter_avatar: string | null
    tournament_id: string
    slug: string
    title: string
    starts_at: string
    timezone: string
    cover_image: string | null
    game_name: string
    game_slug: string
    entry_fee_cents: number
    prize_pool_cents: number
    accent_from: string
    accent_to: string
  }>).map((r) => ({
    inviteId: r.invite_id,
    message: r.message,
    invitedAt: r.invited_at,
    inviter: { gamertag: r.inviter_gamertag, avatar: r.inviter_avatar },
    tournamentId: r.tournament_id,
    slug: r.slug,
    title: r.title,
    startsAt: r.starts_at,
    timezone: r.timezone,
    coverImage: r.cover_image,
    gameName: r.game_name,
    gameSlug: r.game_slug,
    entryFeeCents: r.entry_fee_cents,
    prizePoolCents: r.prize_pool_cents,
    accent: { from: r.accent_from, to: r.accent_to },
  }))
}

/** All invites for an event, for the organizer's manage page. */
export async function getInvitesForOrganizer(
  tournamentId: string
): Promise<SentInvite[]> {
  const { data, error } = await supabase.rpc('tournament_invites_for_organizer', {
    p_tournament_id: tournamentId,
  })
  if (error || !data) {
    if (error) console.error('getInvitesForOrganizer error:', error.message)
    return []
  }
  return (data as Array<{
    invite_id: string
    invitee_id: string
    gamertag: string
    avatar: string | null
    status: string
    message: string | null
    invited_at: string
    responded_at: string | null
  }>).map((r) => ({
    inviteId: r.invite_id,
    inviteeId: r.invitee_id,
    gamertag: r.gamertag,
    avatar: r.avatar,
    status: r.status as InviteStatus,
    message: r.message,
    invitedAt: r.invited_at,
    respondedAt: r.responded_at,
  }))
}

/** Sidebar counts + the organizer's other events for the picker modal. */
export async function getInviteSources(
  tournamentId: string
): Promise<InviteSources | null> {
  const { data, error } = await supabase.rpc('invite_candidate_sources', {
    p_tournament_id: tournamentId,
  })
  if (error || !data) {
    if (error) console.error('getInviteSources error:', error.message)
    return null
  }
  const r = data as {
    matches: number
    pastGuests: number
    events: { id: string; title: string; startsAt: string; guests: number }[] | null
  }
  return {
    matches: r.matches ?? 0,
    pastGuests: r.pastGuests ?? 0,
    events: r.events ?? [],
  }
}

/** One page of invitable people for a chosen source. */
export async function getInviteCandidates(
  tournamentId: string,
  source: InviteSourceKey,
  opts: { eventId?: string; query?: string; limit?: number; offset?: number } = {}
): Promise<InviteCandidate[]> {
  const { data, error } = await supabase.rpc('invite_candidates', {
    p_tournament_id: tournamentId,
    p_source: source,
    p_source_event_id: opts.eventId ?? null,
    p_query: opts.query ?? null,
    p_limit: opts.limit ?? 30,
    p_offset: opts.offset ?? 0,
  })
  if (error || !data) {
    if (error) console.error('getInviteCandidates error:', error.message)
    return []
  }
  return (data as Array<{ user_id: string; gamertag: string; avatar: string | null }>).map(
    (r) => ({ userId: r.user_id, gamertag: r.gamertag, avatar: r.avatar })
  )
}
